import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './hooks/useAuth';

// Layouts
import { AdminLayout } from './components/layouts/AdminLayout';
import { AuthLayout } from './components/layouts/AuthLayout';

// Pages
import { InvoicesPage } from './features/invoices';
import { DashboardPage } from './pages/DashboardPage';
import { SettingsPage } from './pages/SettingsPage';
import { WhatsAppPage } from './pages/WhatsAppPage';
import { ApplicationReviewPage } from './pages/applications/ApplicationReviewPage';
import { ApplicationSuccessPage } from './pages/applications/ApplicationSuccessPage';
import { ApplicationsPage } from './pages/applications/ApplicationsPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { LoginPage } from './pages/auth/LoginPage';
import { BookingDetailPage } from './pages/bookings/BookingDetailPage';
import { BookingsPage } from './pages/bookings/BookingsPage';
import { CalendarPage } from './pages/calendar/CalendarPage';
import { PaymentPlanDetailPage } from './pages/payment-plans/PaymentPlanDetailPage';
import { PaymentPlansPage } from './pages/payment-plans/PaymentPlansPage';
import { QuoteProcessPage } from './pages/quotes/QuoteProcessPage';
import { QuoteViewPage } from './pages/quotes/QuoteViewPage';
import { QuotesPage } from './pages/quotes/QuotesPage';
import { SpaceCreatePage } from './pages/spaces/SpaceCreatePage';
import { SpaceEditPage } from './pages/spaces/SpaceEditPage';
import { SpacesPage } from './pages/spaces/SpacesPage';
import { UsersPage } from './pages/users/UsersPage';

function App() {
  // #region agent log
  // Proves whether `ApplicationReviewPage` is undeclared vs declared but undefined.
  (() => {
    const logPayload = (() => {
      try {
        const typeofResult = Function('return typeof ApplicationReviewPage')();
        const valueType = (() => {
          try {
            const value = Function('return ApplicationReviewPage')();
            return typeof value;
          } catch {
            return 'unreachable';
          }
        })();

        const globalPropertyType = typeof (globalThis as any).ApplicationReviewPage;
        return { typeofResult, valueType, globalPropertyType };
      } catch (error) {
        return { error: error instanceof Error ? error.message : String(error) };
      }
    })();

    fetch('http://127.0.0.1:7885/ingest/daa6c445-ecb8-4db9-bcd8-6dbd6a9b6253', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': 'acc5f4',
      },
      body: JSON.stringify({
        sessionId: 'acc5f4',
        runId: 'post-fix',
        hypothesisId: 'H1-missing-or-unresolved-import',
        location: 'admin/src/App.tsx:App()',
        message: 'Check ApplicationReviewPage symbol resolution before render',
        data: logPayload,
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  })();
  // #endregion agent log

  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <Routes>
        {/* Route publique pour visualisation des devis (sans authentification) */}
        <Route path="/quotes/:id/view" element={<QuoteViewPage />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* Protected admin routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Réservations */}
            <Route path="/bookings" element={<BookingsPage />} />
            <Route path="/bookings/:id" element={<BookingDetailPage />} />

            {/* Calendrier */}
            <Route path="/calendar" element={<CalendarPage />} />

            {/* Facturation */}
            <Route path="/invoices" element={<InvoicesPage />} />

            {/* Candidatures */}
            <Route path="/applications" element={<ApplicationsPage />} />
            <Route path="/applications/:id" element={<ApplicationReviewPage />} />
            <Route path="/applications/:id/success" element={<ApplicationSuccessPage />} />

            {/* Devis */}
            <Route path="/quotes" element={<QuotesPage />} />
            <Route path="/quotes/:id/process" element={<QuoteProcessPage />} />

            {/* Espaces */}
            <Route path="/spaces" element={<SpacesPage />} />
            <Route path="/spaces/new" element={<SpaceCreatePage />} />
            <Route path="/spaces/:id/edit" element={<SpaceEditPage />} />

            {/* Utilisateurs */}
            <Route path="/users" element={<UsersPage />} />

            {/* Paramètres */}
            <Route path="/settings" element={<SettingsPage />} />

            {/* WhatsApp IA */}
            <Route path="/whatsapp" element={<WhatsAppPage />} />

            {/* Plans de Paiement */}
            <Route path="/payment-plans" element={<PaymentPlansPage />} />
            <Route path="/payment-plans/:id" element={<PaymentPlanDetailPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
